import { useEffect, useState } from 'react';

import useProductsStore from 'hooks/stores/useProductsStore';

interface UseAssetPreviewProps {
  assetId: string;
  sideId: string;
  initialUrl?: string;
}

interface UseAssetPreviewReturn {
  imageUrl: string | undefined;
  isLoading: boolean;
  hasError: boolean;
  onImageLoad: () => void;
  onImageError: () => void;
}

const useAssetPreview = ({
  assetId,
  sideId,
  initialUrl,
}: UseAssetPreviewProps): UseAssetPreviewReturn => {
  // Get side data to check if we already have a preview URL. This hook always renders a small,
  // fixed-size thumbnail box, so the small previewThumbnailUrl is preferred over the full-size
  // previewImageUrl (used by the central editor) whenever it's available.
  const side = useProductsStore((state) => state.getSide(sideId));
  const sidePreviewUrl =
    side?.previewThumbnailUrl && side.previewThumbnailUrl !== ''
      ? side.previewThumbnailUrl
      : side?.previewImageUrl;
  const hasInitialPreview =
    (sidePreviewUrl && sidePreviewUrl !== '') || (initialUrl && initialUrl !== '');

  const [isImageLoading, setIsImageLoading] = useState(hasInitialPreview);
  const [hasError, setHasError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(initialUrl);

  // Use side preview URL if available and not empty, otherwise fall back to initialUrl
  const imageUrl = sidePreviewUrl && sidePreviewUrl !== '' ? sidePreviewUrl : initialUrl;

  // Update image loading state only when URL actually changes
  useEffect(() => {
    if (imageUrl !== currentUrl) {
      setCurrentUrl(imageUrl);
      if (imageUrl && imageUrl !== '') {
        setIsImageLoading(true); // New URL, wait for image to load
        setHasError(false);
      } else {
        setIsImageLoading(false);
      }
    }
  }, [imageUrl, currentUrl]);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsImageLoading(false);
  };

  // Show loader if: no image URL OR empty string OR image is still loading
  const isLoading: boolean = Boolean(!imageUrl || imageUrl === '' || isImageLoading);

  return {
    imageUrl,
    isLoading,
    hasError,
    onImageLoad: handleImageLoad,
    onImageError: handleImageError,
  };
};

export default useAssetPreview;
